import { defineStore } from '@/utils/store'
import { reqApiAgentIndexConfig, reqApiAgentIndexGetPlatformConfig } from '@apis/ok'
import { isDev } from '@/env'
import { useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { getDevice } from '@/utils'
import useCurrency from '@/hooks/user/useCurrency'
import useNationList from '@/hooks/user/useNationList'
import { useUpdateEffect } from 'ahooks'

export type ConfigState = {
  // 是否正在獲取
  fetching: boolean
  // 配置
  config: Partial<OK_API_AGENT_INDEX_CONFIG.RES['t']> & {
    // 初始化狀態，判斷是否為快取用
    initialize: boolean
    currencySign: string
    currentCurrencyName: string
  }
  currencyListAll?: OK_API_AGENT_CURRENCY_LISTALL.RES['rows'] // 取得所有币别
  nationList?: OK_API_AGENT_NATION_LIST.RES['rows'] // 启用中所有国家 列表
  // 登录来源（0：WEB, 1：MOBILE_BROWSER, 2：ANDROID, 3：IOS）
  loginSrc: string
  // 设备 1PC 2H5
  device: number
  // 获取平台配置 & 圖片地址
  platformConfig?: OK_API_AGENT_INDEX_GETPLATFORMCONFIG.RES['t'] // 平台配置
}

/**
 * 網站設定 Config Store
 */
export const [useConfigStore, setConfigState] = defineStore<ConfigState>(
  {
    fetching: false,
    config: {
      initialize: false,
      currencySign: '',
      currentCurrencyName: '',
    },
    loginSrc: String(getDevice() === 'PC' ? 0 : 1),
    device: getDevice() === 'PC' ? 1 : 2,
  },
  {
    name: 'CONFIG',
    partialize: (state) => ({ config: { ...state.config, initialize: false } }) as ConfigState,
  },
)

/** 預載 Config 組件化 */
export const InitConfig = ({ locale, token }: InitGlobalComponentProps) => {
  /** Fetch */
  const { data } = useQuery({
    queryKey: ['/config', locale, token],
    queryFn: ({ signal }) => reqApiAgentIndexConfig.call({ signal }),
    refetchOnMount: !isDev,
    staleTime: 60 * 1000,
  })

  const { data: platformConfigData } = useQuery({
    queryKey: ['/index/getPlatformConfig', locale, token],
    queryFn: ({ signal }) => reqApiAgentIndexGetPlatformConfig.call({ signal }, { cfgName: 'resServerHost' }),
    refetchOnMount: !isDev,
    staleTime: 60 * 1000,
    enabled: !!token,
  })
  const { currencySign, currencyListAll, currentCurrencyName } = useCurrency()
  const { nationList } = useNationList()

  /** Update */
  useEffect(() => {
    if (!data || !currencySign) return
    setConfigState({
      config: {
        ...data.t,
        currencySign,
        currentCurrencyName,
        // 初始化完成
        initialize: true,
      },
      currencyListAll,
      nationList,
    })
  }, [data, currencyListAll, nationList, currentCurrencyName])

  useUpdateEffect(() => {
    setConfigState({
      platformConfig: platformConfigData?.t,
    })
  }, [platformConfigData])

  return null
}
